// CheckInDayGrid.tsx

import "./CheckInDayGrid.scss";
import { z } from "zod";
import { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheck,
    faXmark,
} from "@awesome.me/kit-f3e20eab48/icons/classic/solid";
import HeartIcon from "../../../../../../../assets/icons/heart-icon.svg?react";
import { ExtendedFeedItemSchema } from "../../../../../../../schemas/slices/FeedSliceSchema";
import { useTranslation } from "react-i18next";

// Narrow the local union to the CHI item the grid is used for
type ExtFeedItem = z.infer<typeof ExtendedFeedItemSchema>;
type ChiFeedItem = Extract<
    ExtFeedItem,
    { productType: "PARTNER_CAMPAIGN_CHI" }
>;

interface CheckInDayGridProps {
    createdAt?: number; // campaign start ts (ms). If missing => all "locked"
    daysCheckedIn?: number[]; // check-in timestamps (ms)
    firstCheckInTz?: string; // IANA TZ (e.g. "Europe/Amsterdam")
    absencesLeft?: number; // reserved for later
    task?: ChiFeedItem; // for raw.check_in_campaign.required_days fallback
    failed?: boolean; // show overlay when misses exceed available lives
}

const DAY_MS = 24 * 60 * 60 * 1000;

function getUserTZFallback() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
        return "UTC";
    }
}

function ymdParts(ts: number, tz: string) {
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: tz,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(new Date(ts));
    const map: Record<string, string> = {};
    for (const p of parts) map[p.type] = p.value;
    const y = Number(map.year);
    const m = Number(map.month);
    const d = Number(map.day);
    return { y, m, d };
}

/** Timezone-stable serial day based on local Y-M-D */
function serialDay(ts: number, tz: string) {
    const { y, m, d } = ymdParts(ts, tz);
    return Math.floor(Date.UTC(y, m - 1, d) / DAY_MS);
}
function tsFromSerialDay(sd: number) {
    return sd * DAY_MS;
}

// Take a locale weekday label (often "Mon", "Mo.", "lunes", etc.)
// and reduce to 2 visible letters (Unicode-safe), trimming trailing punctuation.
function twoLetterAbbr(label: string) {
    const cleaned = label.replace(/\P{L}+$/u, ""); // drop trailing non-letters (e.g., ".")
    const letters = Array.from(cleaned); // iterate by code points, not UTF-16 units
    return letters.slice(0, 2).join("");
}

export default function CheckInDayGrid({
    createdAt,
    daysCheckedIn,
    firstCheckInTz,
    absencesLeft,
    task,
    failed,
}: CheckInDayGridProps) {
    const tz = firstCheckInTz || getUserTZFallback();
    const { t } = useTranslation();
    // Source of truth for how many days are rendered:
    // 1) server raw.check_in_campaign.required_days
    // 2) relation.requiredDays (client-side normalized)
    // 3) default 7
    const daysTotal =
        task?.raw?.check_in_campaign?.required_days ??
        task?.relation?.requiredDays ??
        7;

    const { started, baseSerial, nowSerial, checkedIndices, weekdayOf } =
        useMemo(() => {
            const haveCreatedAt =
                !!createdAt && Number.isFinite(createdAt) && createdAt! > 0;
            const haveChecks = (daysCheckedIn?.length || 0) > 0;
            const firstTs = haveCreatedAt
                ? (createdAt as number)
                : haveChecks
                  ? Math.min(...(daysCheckedIn as number[]))
                  : undefined;
            const started = Number.isFinite(firstTs as number);
            const baseSerial = started
                ? serialDay(firstTs as number, tz)
                : serialDay(Date.now(), tz);
            const nowSerial = serialDay(Date.now(), tz);

            const checkedIndices = new Set<number>();
            if (started && (daysCheckedIn?.length || 0) > 0) {
                for (const ts of daysCheckedIn!) {
                    const idx = serialDay(ts, tz) - baseSerial;
                    // Only mark checks that land inside the rendered window
                    if (idx >= 0 && idx < daysTotal) {
                        checkedIndices.add(idx);
                    }
                }
            }

            // Use "short" so we can derive a 2-letter abbreviation reliably
            const weekdayShortFmt = new Intl.DateTimeFormat(undefined, {
                timeZone: tz,
                weekday: "short",
            });
            const weekdayOf = (idxSerial: number) =>
                weekdayShortFmt.format(new Date(tsFromSerialDay(idxSerial)));

            return {
                started,
                baseSerial,
                nowSerial,
                checkedIndices,
                weekdayOf,
            };
        }, [createdAt, daysCheckedIn, tz, daysTotal]);

    return (
        <div className="check-in-day-grid">
            <div className="cidg">
                {Array.from({ length: daysTotal }).map((_, i) => {
                    const daySerial = baseSerial + i;

                    const isChecked = started && checkedIndices.has(i);
                    const isPast = started && daySerial < nowSerial;
                    const isToday = started && daySerial === nowSerial;

                    // One of: "locked" | "checked" | "missed" | "today" | "pending"
                    const stateClass = !started
                        ? "locked"
                        : isChecked
                          ? "checked"
                          : isPast
                            ? "missed"
                            : isToday
                              ? "today"
                              : "pending";

                    const rawWeekday = weekdayOf(daySerial);
                    const weekday = isToday
                        ? "Today"
                        : twoLetterAbbr(rawWeekday);

                    return (
                        <div className={`cidg__item ${stateClass}`} key={i}>
                            <div className="cidg__item__indicator">
                                {isChecked && (
                                    <FontAwesomeIcon icon={faCheck} />
                                )}
                                {/* {isPast && ( */}
                                {stateClass === "missed" && (
                                    <FontAwesomeIcon icon={faXmark} />
                                )}
                            </div>
                            <div className="cidg__item__texts">
                                <div className="cidg__item__texts__day">
                                    {t("CheckInDayGrid.day")} {i + 1}
                                </div>
                                <div className="cidg__item__texts__weekday">
                                    {weekday}
                                </div>
                            </div>

                            {stateClass === "missed" && (
                                <div className="cidg__item__life-lost">
                                    <p>-1</p>
                                    <HeartIcon />
                                </div>
                            )}
                        </div>
                    );
                })}

                {daysTotal % 2 === 1 && (
                    <div
                        className="cidg__item cidg__summary"
                        key="cidg-summary"
                    >
                        <div className="cidg__summary__container">
                            <span className="cidg__summary__label">
                                {t("CheckInDayGrid.livesLeft")}
                            </span>
                            <div className="cidg__summary__value">
                                <HeartIcon />
                                <p>
                                    {typeof absencesLeft === "number"
                                        ? absencesLeft
                                        : "—"}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {failed && (
                    <div className="cidg__failed-overlay">
                        <div className="cidg__failed-overlay__content">
                            <p>
                                {t("CheckInDayGrid.youHaveMissedTooManyDays")}
                            </p>
                            <p>{t("CheckInDayGrid.checkInChallengeFailed")}</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
